import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Delete } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import packageService, { TestPackage } from '../services/packageService';
import { itemService } from '../services/itemService';

/** Questions are stored as HTML; show them rendered and trimmed to one line. */
const Preview: React.FC<{ html?: string }> = ({ html }) => (
  <Typography
    variant="body2"
    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html || '<em>Untitled question</em>') }}
  />
);

const PackageQuestionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [pack, setPack] = useState<TestPackage | null>(null);
  const [bank, setBank] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [loadedPack, items] = await Promise.all([
        packageService.getPackage(id),
        // One page large enough to cover the bank; the picker filters client-side.
        itemService.getItems({} as any, { page: 1, pageSize: 1000 } as any),
      ]);
      setPack(loadedPack);
      setBank(items?.items ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load test');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const attachedIds = useMemo(
    () => new Set((pack?.questions ?? []).map((q) => q.id || q.questionId).filter(Boolean) as string[]),
    [pack]
  );

  const attached = useMemo(
    () => bank.filter((item) => attachedIds.has(item.id)),
    [bank, attachedIds]
  );

  // The bank minus what is already on this test, narrowed by the search box.
  const available = useMemo(() => {
    const term = search.trim().toLowerCase();
    return bank
      .filter((item) => !attachedIds.has(item.id))
      .filter((item) => !term || (item.questionPreview || '').toLowerCase().includes(term))
      .slice(0, 100);
  }, [bank, attachedIds, search]);

  const runAction = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
      setSelected([]);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate('/tests')}><ArrowBack /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{pack?.name || 'Test'}</Typography>
        <Chip size="small" label={`${attachedIds.size} questions`} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Paper sx={{ flex: '1 1 420px', minWidth: 0, p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>On this test</Typography>
          <Divider sx={{ mb: 1 }} />
          {attached.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No questions yet — add them from the item bank
            </Typography>
          )}
          {attached.map((item) => (
            <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}><Preview html={item.questionPreview} /></Box>
              <IconButton
                size="small" color="error" disabled={busy}
                title="Remove from test"
                onClick={() => runAction(() => packageService.removeQuestions(id!, [item.id]))}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Paper>

        <Paper sx={{ flex: '1 1 420px', minWidth: 0, p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Item bank</Typography>
          <TextField
            fullWidth size="small" placeholder="Search questions..."
            value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 1 }}
          />
          <Button
            variant="contained" size="small" sx={{ mb: 1 }}
            disabled={selected.length === 0 || busy}
            onClick={() => runAction(() => packageService.addQuestions(id!, selected))}
          >
            Add {selected.length > 0 ? `${selected.length} ` : ''}to test
          </Button>
          <Divider sx={{ mb: 1 }} />
          {available.map((item) => (
            <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                size="small"
                checked={selected.includes(item.id)}
                onChange={(e) =>
                  setSelected((prev) =>
                    e.target.checked ? [...prev, item.id] : prev.filter((s) => s !== item.id)
                  )
                }
              />
              <Box sx={{ flex: 1, minWidth: 0 }}><Preview html={item.questionPreview} /></Box>
            </Box>
          ))}
          {available.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              Nothing left to add
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default PackageQuestionsPage;
